import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowLeft, GripVertical, Loader2, Calendar, Eye, EyeOff } from "lucide-react";
import ImageUpload from "@/components/common/ImageUpload";

// Curated India-first category badges (fully manageable, but choose one from this list).
export const BADGE_PRESETS = [
  { key: "new-launch",    label: "New Launch",      emoji: "\ud83d\ude97", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "expert-review", label: "Expert Review",   emoji: "\u2b50",       cls: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "ev-special",    label: "EV Special",      emoji: "\u26a1",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "trending",      label: "Trending",        emoji: "\ud83d\udd25", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  { key: "compare",       label: "Compare",         emoji: "\u2696\ufe0f", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { key: "editors-pick",  label: "Editor\u2019s Pick", emoji: "\ud83c\udfc6", cls: "bg-yellow-50 text-yellow-800 border-yellow-200" },
  { key: "festival-offer",label: "Festival Offer",  emoji: "\ud83c\udf89", cls: "bg-pink-50 text-pink-700 border-pink-200" },
  { key: "auto-expo",     label: "Auto Expo",       emoji: "\ud83c\udfdf\ufe0f", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  { key: "sponsored",     label: "Sponsored",       emoji: "\ud83d\udcc8", cls: "bg-slate-100 text-slate-700 border-slate-200" },
];
export const badgeByKey = (k) => BADGE_PRESETS.find((b) => b.key === k);

const ACCENT_CHOICES = [
  { label: "Blue-cyan", value: "from-white via-[#EFF6FF] to-[#ECFEFF]" },
  { label: "Green",     value: "from-white via-[#F1FAF5] to-[#ECFEFF]" },
  { label: "Amber",     value: "from-white via-[#FFF7ED] to-[#FEF3C7]" },
  { label: "Purple",    value: "from-white via-[#EFF6FF] to-[#F5F3FF]" },
];

const CTA_PRESETS = [
  "View Details", "Read Review", "Compare Cars",
  "Explore Now", "Learn More", "Book Test Drive", "See Offers",
];

const empty = {
  order: 0, eyebrow: "New Launch", eyebrow_icon: "sparkles",
  badge_key: "new-launch", badge_label: "",
  title: "", title_accent: "", subtitle: "", image: "",
  primary_cta_label: "View Details", primary_cta_to: "/cars",
  secondary_cta_label: "", secondary_cta_to: "",
  accent_gradient: ACCENT_CHOICES[0].value, status: "published",
  starts_at: null, ends_at: null,
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  try { const d = new Date(iso); const pad = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; } catch { return ""; }
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

export default function HeroSlidesAdmin() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const dragIdx = useRef(null);
  const dragOverIdx = useRef(null);

  const load = () => {
    setBusy(true);
    api.get("/admin/hero-slides").then((r) => setRows(r.data)).finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    if (!editing.title || !editing.image) return toast.error("Title and image are required");
    try {
      const body = { ...editing };
      if (!body.badge_label) delete body.badge_label; // empty means use preset label
      if (editing.id) await api.put(`/admin/hero-slides/${editing.id}`, body);
      else await api.post("/admin/hero-slides", body);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this slide?")) return;
    await api.delete(`/admin/hero-slides/${id}`);
    load();
  };

  const toggleStatus = async (row) => {
    const next = row.status === "published" ? "draft" : "published";
    await api.put(`/admin/hero-slides/${row.id}`, { ...row, status: next });
    load();
  };

  // ---- HTML5 drag-and-drop reorder ----
  const onDragStart = (i) => { dragIdx.current = i; };
  const onDragOver = (e, i) => { e.preventDefault(); dragOverIdx.current = i; };
  const onDrop = async () => {
    const from = dragIdx.current; const to = dragOverIdx.current;
    dragIdx.current = null; dragOverIdx.current = null;
    if (from == null || to == null || from === to) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    // optimistic update + persist
    setRows(next.map((r, i) => ({ ...r, order: i })));
    try {
      await api.post("/admin/hero-slides/reorder", { ids: next.map((r) => r.id) });
    } catch (e) {
      toast.error("Reorder failed");
      load();
    }
  };

  if (editing) {
    const badge = badgeByKey(editing.badge_key);
    return (
      <div data-testid="admin-hero-edit">
        <button onClick={() => setEditing(null)} className="text-sm text-blue-600 inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4"/> Back to hero slides
        </button>
        <h1 className="font-display text-3xl tracking-tighter">{editing.id ? "Edit slide" : "New slide"}</h1>

        {/* Live preview */}
        {editing.image && (
          <div className="mt-6 rounded-3xl border border-[#E5E7EB] overflow-hidden bg-white">
            <div className={`relative bg-gradient-to-r ${editing.accent_gradient} p-8`}>
              <div className="flex flex-col-reverse lg:flex-row lg:items-center gap-6">
                <div className="flex-1 min-w-0">
                  {badge && (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium ${badge.cls}`}>
                      <span>{badge.emoji}</span> {editing.badge_label || badge.label}
                    </span>
                  )}
                  <div className="font-display text-3xl sm:text-4xl mt-3 tracking-tight leading-tight">{editing.title || "Slide title"}</div>
                  {editing.title_accent && <div className="font-display text-2xl sm:text-3xl text-[#2563EB] tracking-tight">{editing.title_accent}</div>}
                  {editing.subtitle && <p className="text-sm text-muted-foreground mt-3 max-w-lg">{editing.subtitle}</p>}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {editing.primary_cta_label && <span className="btn-primary h-9 px-4 text-sm inline-flex items-center rounded-full">{editing.primary_cta_label}</span>}
                    {editing.secondary_cta_label && <span className="btn-secondary h-9 px-4 text-sm inline-flex items-center rounded-full">{editing.secondary_cta_label}</span>}
                  </div>
                </div>
                <div className="w-full lg:w-64 aspect-[4/3] rounded-2xl overflow-hidden border border-[#E5E7EB] shrink-0">
                  <img src={editing.image} alt={editing.title} className="w-full h-full object-cover"/>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Status">
                <select data-testid="hero-status" value={editing.status}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })} className={inp}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </Field>
              <Field label="Order">
                <input data-testid="hero-order" type="number" value={editing.order}
                  onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || "0", 10) })}
                  className={inp}/>
              </Field>
              <Field label="Accent gradient">
                <select data-testid="hero-accent" value={editing.accent_gradient}
                  onChange={(e) => setEditing({ ...editing, accent_gradient: e.target.value })} className={inp}>
                  {ACCENT_CHOICES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </Field>
            </div>

            <div className="rounded-3xl border border-[#E5E7EB] bg-white p-4 space-y-3" data-testid="hero-badge-section">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Category badge</div>
              <div className="flex flex-wrap gap-2">
                {BADGE_PRESETS.map((b) => (
                  <button key={b.key} type="button" data-testid={`hero-badge-${b.key}`}
                    onClick={() => setEditing({ ...editing, badge_key: b.key, eyebrow: editing.eyebrow || b.label })}
                    className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full border text-xs transition-all ${
                      editing.badge_key === b.key ? `${b.cls} ring-2 ring-offset-1 ring-[#2563EB]` : `${b.cls} opacity-70 hover:opacity-100`
                    }`}>
                    <span>{b.emoji}</span> {b.label}
                  </button>
                ))}
              </div>
              <Field label="Custom badge label (optional — overrides preset text)">
                <input data-testid="hero-badge-label" value={editing.badge_label || ""}
                  onChange={(e) => setEditing({ ...editing, badge_label: e.target.value })}
                  placeholder={badge?.label || ""} className={inp}/>
              </Field>
            </div>

            <Field label="Title *">
              <input data-testid="hero-title" value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inp}/>
            </Field>
            <Field label="Title accent (shown in gradient below title)">
              <input data-testid="hero-title-accent" value={editing.title_accent}
                onChange={(e) => setEditing({ ...editing, title_accent: e.target.value })} className={inp}/>
            </Field>
            <Field label="Subtitle">
              <textarea data-testid="hero-subtitle" value={editing.subtitle}
                onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
                className={`${inp} min-h-[80px] py-2`}/>
            </Field>

            <div className="rounded-3xl border border-[#E5E7EB] bg-white p-4 space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Call-to-action</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Primary CTA label">
                  <select data-testid="hero-cta-primary" value={editing.primary_cta_label}
                    onChange={(e) => setEditing({ ...editing, primary_cta_label: e.target.value })} className={inp}>
                    {[...new Set([editing.primary_cta_label, ...CTA_PRESETS])].filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Primary CTA destination">
                  <input data-testid="hero-cta-primary-to" value={editing.primary_cta_to}
                    onChange={(e) => setEditing({ ...editing, primary_cta_to: e.target.value })}
                    placeholder="/cars/tata-harrier-ev" className={inp}/>
                </Field>
                <Field label="Secondary CTA label">
                  <input data-testid="hero-cta-secondary" value={editing.secondary_cta_label || ""}
                    onChange={(e) => setEditing({ ...editing, secondary_cta_label: e.target.value })} className={inp}/>
                </Field>
                <Field label="Secondary CTA destination">
                  <input data-testid="hero-cta-secondary-to" value={editing.secondary_cta_to || ""}
                    onChange={(e) => setEditing({ ...editing, secondary_cta_to: e.target.value })} className={inp}/>
                </Field>
              </div>
            </div>

            <div className="rounded-3xl border border-[#E5E7EB] bg-white p-4 space-y-3" data-testid="hero-schedule-section">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Schedule (optional)</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Starts at">
                  <div className="relative">
                    <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"/>
                    <input data-testid="hero-starts-at" type="datetime-local" value={toLocalInput(editing.starts_at)}
                      onChange={(e) => setEditing({ ...editing, starts_at: fromLocalInput(e.target.value) })}
                      className={`${inp} pl-9`}/>
                  </div>
                </Field>
                <Field label="Ends at">
                  <div className="relative">
                    <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"/>
                    <input data-testid="hero-ends-at" type="datetime-local" value={toLocalInput(editing.ends_at)}
                      onChange={(e) => setEditing({ ...editing, ends_at: fromLocalInput(e.target.value) })}
                      className={`${inp} pl-9`}/>
                  </div>
                </Field>
              </div>
              <div className="text-[11px] text-muted-foreground">Leave blank to show indefinitely. Great for festival offers, Auto Expo weeks etc.</div>
            </div>
          </div>

          <div className="space-y-4">
            <Field label="Slide image *">
              <ImageUpload value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} folder="hero" testId="hero-image"/>
            </Field>
            <button onClick={save} data-testid="hero-save" className="btn-primary w-full h-11 text-sm inline-flex items-center justify-center gap-2">
              <Save className="w-4 h-4"/> Save slide
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-hero">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tighter">Hero Slides</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage the rotating homepage banner. Drag to reorder, toggle to publish/unpublish, schedule for festivals & campaigns.</p>
        </div>
        <button data-testid="hero-new" onClick={() => setEditing({ ...empty, order: rows.length })} className="btn-primary h-10 px-4 text-sm inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> Add banner
        </button>
      </div>
      <div className="mt-6 space-y-3" onDragOver={(e) => e.preventDefault()}>
        {busy ? (
          <div className="grid place-items-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin"/></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground text-sm">No slides yet. Add your first banner.</div>
        ) : rows.map((s, i) => {
          const badge = badgeByKey(s.badge_key);
          const now = new Date();
          const startsFuture = s.starts_at && new Date(s.starts_at) > now;
          const endsPast = s.ends_at && new Date(s.ends_at) < now;
          return (
            <div key={s.id} data-testid={`hero-row-${s.id}`} draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={(e) => onDragOver(e, i)}
              onDrop={onDrop}
              className="rounded-3xl border border-[#E5E7EB] bg-white p-4 flex gap-4 items-center hover:border-blue-300 transition-colors cursor-grab active:cursor-grabbing">
              <GripVertical className="w-5 h-5 text-muted-foreground shrink-0"/>
              <img src={s.image} alt="" className="w-32 h-20 object-cover rounded-xl border border-[#E5E7EB]"/>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {badge && (
                    <span className={`inline-flex items-center gap-1 px-2 h-6 rounded-full border text-[11px] ${badge.cls}`}>
                      <span>{badge.emoji}</span> {s.badge_label || badge.label}
                    </span>
                  )}
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${s.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>{s.status}</span>
                  {startsFuture && <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">scheduled</span>}
                  {endsPast && <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">expired</span>}
                  <span className="text-[10px] text-muted-foreground">order {s.order}</span>
                </div>
                <div className="font-display text-lg mt-1 truncate">{s.title} <span className="text-muted-foreground">{s.title_accent}</span></div>
                <div className="text-xs text-muted-foreground line-clamp-1">{s.subtitle}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => toggleStatus(s)} data-testid={`hero-toggle-${s.id}`}
                  className="h-8 w-8 rounded-lg border border-[#E5E7EB] grid place-items-center hover:bg-[#F3F6FA]" title={s.status === "published" ? "Unpublish" : "Publish"}>
                  {s.status === "published" ? <Eye className="w-3 h-3 text-emerald-600"/> : <EyeOff className="w-3 h-3 text-muted-foreground"/>}
                </button>
                <button onClick={() => setEditing({ ...s })} data-testid={`hero-edit-${s.id}`} className="text-sm text-blue-600 hover:underline px-2">Edit</button>
                <button onClick={() => del(s.id)} data-testid={`hero-del-${s.id}`} className="text-sm text-rose-600 hover:underline inline-flex items-center gap-1 px-2"><Trash2 className="w-3 h-3"/> Delete</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const inp = "w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#2563EB]";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
